<template>
  <div :class="['vue-tel-input', wrapperClasses, { disabled: disabled }]">
    <b-field :label="labels.country" class="form-field country-form-field">
      <multiselect-field v-if="multiselectCountryOptions"
                         :options="multiselectCountryOptions"
                         :value="activeCountry"
                         :taggable="false"
                         :multiple="false"
                         :iconField="false"
                         :close-on-select="true"
                         :clear-on-select="true"
                         :showOptionsList="true"
                         :showLabels="false"
                         :searchable="false"
                         :select-label="''"
                         :has-validation-error="!!error"
                         label="name"
                         @select="selectCountry($event); setCursorToInput()">
        <div slot="selected-option-html" :class="activeCountry.iso2.toLowerCase()" class="iti-flag"></div>
        <div slot="option" slot-scope="props">
          <div :class="props.option.iso2 ? props.option.iso2.toLowerCase() : ''" class="iti-flag flag-option"></div>
          <div>{{ props.option.name }}</div>
          <div class="dial-code-option">+{{ props.option.dialCode }}</div>
        </div>
      </multiselect-field>
    </b-field>
    <b-field :type="error ? 'is-danger' : ''" :message="error" :label="labels.phone" class="form-field">
      <input
        ref="input"
        v-model="phone"
        :disabled="disabled"
        :required="required"
        :autocomplete="autocomplete"
        :name="name"
        :class="inputClassesWithError"
        :maxlength="maxLen"
        type="tel"
        class="input"
        @blur="onBlur"
        @input="onInput"
        @keyup.enter="onEnter"
        @keyup.space="onSpace"
        @keyup="formatPhoneUsingPlus"
      >
    </b-field>
  </div>
</template>

<script>

import 'vue-tel-input/dist/vue-tel-input.css';

import {trimStart} from 'lodash';
import VueTelInput from 'vue-tel-input';

import MultiselectField from '../MultiselectField';

export default {
  extends: VueTelInput,
  props: {
    error: String,
    disabledFormatting: {
      type: Boolean,
      default: true
    }
  },
  mounted() {
    if (this.phone) {
      this.changeDataOnInitWithValue();
    }

    this.changeCountryOptionsWidth();
  },
  data() {
    return {
      topCountries: ['cn', 'hk', 'sg', 'us'],
      priorityCountries: {
        '7': 'ru',
        '1': 'us'
      }
    };
  },
  watch: {
    activeCountry(value, oldValue) {
      this.changePhoneOnCountrySelect(value, oldValue);
    }
  },
  computed: {
    errorClass() {
      return {'is-danger': !!this.error};
    },
    inputClassesWithError() {
      return this.inputClasses ? [this.inputClasses, this.errorClass] : this.errorClass;
    },
    selectLabel() {
      return this.$gettext('Press enter to select');
    },
    multiselectCountryOptions() {
      let other = [];
      let top = [];
      if (!this.sortedCountries) {
        return [];
      }
      for (let country of this.sortedCountries) {
        if (this.topCountries.includes(country.iso2.toLocaleLowerCase())) {
          top.push(country);
        } else {
          other.push(country);
        }
      }

      return [...top, {isSeparator: true}, ...other];
    },
    labels() {
      return {
        country: this.$gettext('Country'),
        phone: this.$gettext('Phone number')
      };
    }
  },
  methods: {
    onInput() {
      this.formatPhone();
      this.fixSelectCountry();
      this.$emit('input', this.phone, this.response);
    },
    clearPhone() {
      this.phone = '';
    },
    selectCountry(country) {
      this.activeCountry = country;
      if (this.inputOptions && this.inputOptions.showDialCode && country) {
        this.phone = '+' + country.dialCode;
      }
    },
    formatPhone() {
      let phone = this.phone;
      phone = trimStart(phone);

      if (!phone) {
        this.phone = phone;
        return;
      }

      if (!phone.startsWith('+')) {
        phone = `+${phone}`;
      }

      if (this.response && this.response.country) {
        let countryCode = this.response.country.dialCode;
        if (this.phoneRequiresCountryCode(countryCode)) {
          phone = phone.replace(`+${countryCode}`, `+${countryCode} `);
        }
      }

      this.phone = phone;
    },
    changeCountryOptionsWidth() {
      this.$nextTick(() => {
        let fullContainer = document.getElementsByClassName('vue-tel-input')[0];
        const width = `${fullContainer.offsetWidth}px`;
        document.getElementsByClassName('multiselect__content-wrapper')[0].style.width = width;
        document.getElementsByClassName('multiselect__tags')[0].style.height = this.$refs.input.offsetHeight + 'px';
      });
    },
    changePhoneOnCountrySelect(value, oldValue) {
      if (value.dialCode && !this.phone.startsWith(`+${value.dialCode} `)) {
        this.phone = `+${value.dialCode} `;
        this.$emit('input', this.phone, this.response);
      }
    },
    formatPhoneUsingPlus() {
      if (!this.phone.startsWith('+')) {
        this.phone = `+${this.phone}`;
      }
    },
    changeDataOnInitWithValue() {
      this.formatPhoneUsingPlus();
      this.changeActiveCountryByISO2();
      this.onInput();
    },
    changeActiveCountryByISO2() {
      if (this.response && typeof this.response.country === 'string' && this.findCountryByISO2(this.response.country)) {
        this.activeCountry = this.findCountryByISO2(this.response.country);
      }
    },
    phoneRequiresCountryCode(code) {
      const plusCode = `+${code}`;
      return this.phone.startsWith(plusCode) && !this.phone.startsWith(`${plusCode} `) && this.phone !== plusCode;
    },
    setCursorToInput() {
      this.$refs.input.focus();
    },
    fixSelectCountry() {
      const code = this.phone.replace('+', '').replace(' ', '');
      const country = this.findCountryByCode(code);
      if (country) {
        this.activeCountry = country;
      }
    },
    findCountryByCode(code) {
      if (Object.keys(this.priorityCountries).includes(code)) {
        return this.sortedCountries.find(item => item.iso2.toLocaleLowerCase() === this.priorityCountries[code]);
      }
      return this.sortedCountries.find(item => item.dialCode === code);
    },
    findCountryByISO2(iso2) {
      return this.sortedCountries.find(item => item.iso2 === iso2);
    },
    onBlurInput() {
      this.formatPhone();
    }
  },
  components: {MultiselectField}
};
</script>

<style scoped lang="scss">
.vue-tel-input {
  display: flex;
  border-right: none;

  .field {
    margin-bottom: 0;

    &:first-child {
      margin-right: 10px;
    }

    &:last-child {
      width: 100%;
    }

    .flag-option {
      display: flex;
    }

    .dial-code-option {
      margin-left: auto;
    }

    /deep/ .multiselect__tags {
      height: 38px;
      display: flex;
      align-items: center;
    }

    /deep/ .multiselect__select {
      height: 100%;
    }

    /deep/ .multiselect__option {
      > div {
        display: flex;
        align-items: center;

        .flag-option {
          margin-right: 5px;
        }
      }
    }
  }
}
</style>
